import {
  AD_MATERIAL_TYPE,
  AD_MATERIAL_SUB_TYPE,
  Ad,
  AdMaterial,
  AdTrackingEventKey,
  AdTrackingEventUrl,
} from "../types/ads";

const POPUP_IMAGE_SIZE = {
  width: 620,
  height: 760,
} as const;

const SHARE_IMAGE_SIZE = {
  width: 720,
  height: 360,
} as const;

export interface MarketingPopupAd {
  adId: string;
  imageUrl: string;
  imageMaterialId: string;
  shareImageUrl?: string;
  shareText?: string;
  landingUrl?: string;
  trackingEventUrls: AdTrackingEventUrl[];
}

const isMaterialType = (material: AdMaterial, type: string) =>
  material.type?.toLowerCase() === type;

const hasSize = (material: AdMaterial, width: number, height: number) =>
  material.sizeFormat?.w === width && material.sizeFormat?.h === height;

export const normalizeMarketingPopupAd = (ad: Ad): MarketingPopupAd | null => {
  const materials = ad.materials || [];
  const imageMaterials = materials.filter(
    (material) =>
      isMaterialType(material, AD_MATERIAL_TYPE.IMAGE) && Boolean(material.url)
  );
  const popupImage =
    imageMaterials.find((material) =>
      hasSize(material, POPUP_IMAGE_SIZE.width, POPUP_IMAGE_SIZE.height)
    ) || imageMaterials[0];

  if (!popupImage?.url) return null;

  const remainingImages = imageMaterials.filter(
    (material) => material.id !== popupImage.id
  );
  const shareImage =
    remainingImages.find((material) =>
      hasSize(material, SHARE_IMAGE_SIZE.width, SHARE_IMAGE_SIZE.height)
    ) || remainingImages[0];
  const textMaterials = materials.filter(
    (material) =>
      isMaterialType(material, AD_MATERIAL_TYPE.TEXT) && Boolean(material.text)
  );
  const textMaterial =
    textMaterials.find(
      (material) =>
        material.subType?.toLowerCase() === AD_MATERIAL_SUB_TYPE.TEXT
    ) || textMaterials[0];

  return {
    adId: ad.adId,
    imageUrl: popupImage.url,
    imageMaterialId: popupImage.id,
    shareImageUrl: shareImage?.url || undefined,
    shareText: textMaterial?.text || undefined,
    landingUrl: ad.landingUrl || undefined,
    trackingEventUrls: ad.trackingEventUrls || [],
  };
};

export const normalizeMarketingPopupAds = (ads: Ad[] = []) =>
  ads
    .map(normalizeMarketingPopupAd)
    .filter((ad): ad is MarketingPopupAd => ad !== null);

export const getAdTrackingUrl = (
  trackingEventUrls: AdTrackingEventUrl[],
  key: AdTrackingEventKey
) => trackingEventUrls.find((event) => event.key?.toLowerCase() === key)?.value;

export const createVimpressionId = () => {
  const bytes = new Uint8Array(16);

  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const replaceAdTrackingPlaceholders = (
  url: string,
  vimpressionId: string,
  materialId: string
) =>
  url
    .replace(/\$\{VIMPRESSIONID\}/g, vimpressionId)
    .replace(/\$\{MATERIALID\}/g, materialId)
    .replace(
      /\$\{ORIENTATION\}/g,
      window.innerWidth > window.innerHeight ? "landscape" : "portrait"
    );

export const getMarketingPopupTrackingUrl = (
  popup: MarketingPopupAd,
  key: AdTrackingEventKey,
  vimpressionId: string
) => {
  const trackingUrl = getAdTrackingUrl(popup.trackingEventUrls, key);
  if (!trackingUrl) return null;

  return replaceAdTrackingPlaceholders(
    trackingUrl,
    vimpressionId,
    popup.imageMaterialId
  );
};

export const hasShareMaterials = (popup: MarketingPopupAd) =>
  Boolean(popup.shareImageUrl && popup.shareText && popup.landingUrl);
